import { Button, Flex, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { IconButton } from '@gamma/ui/buttons';
import { Input, OptionType, Select } from '@gamma/ui/form-fields';
import { usePrevious } from '@gamma/ui/hooks';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import SearchSharpIcon from '@gamma/ui/icons/SearchSharpIcon';
import { Column, Panel } from '@gamma/ui/layout';
import { REGEX, ROUTES, URLS } from '@investigator/shared/constants';
import { useQueryParams } from '@investigator/shared/hooks/useQueryParams';
import { i18n } from '@investigator/shared/localization';
import { OrgTenantsContext } from '@investigator/shared/providers';
import {
  chakraComponents,
  GroupBase,
  MultiValue,
  OptionProps,
} from 'chakra-react-select';
import { isEqual } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { safeParse } from 'secure-json-parse';
import {
  SetAlertStatusBySearchTermFiltersInput,
  SetAlertStatusScore,
  SetAlertStatusType,
} from '../../../../queries';

const { alertCatalog } = ROUTES;

interface StatusOption {
  value: number;
  label: string;
}

interface ScoreOption {
  value: string;
  label: string;
  min: number;
  max: number;
  severity: ReactNode;
}

interface TypeOption {
  value: string;
  label: string;
}

export interface AlertCatalogForm {
  wildcard: string;
  active: string;
  severity: string;
  category: string;
}

export interface AlertCatalogSearchProps {
  loading: boolean;
  queryTerm: string | null;
  setQuery: (query: string | null) => void;
  setQueryTerm: Dispatch<SetStateAction<string | null>>;
  setPrevQueryTerm: Dispatch<SetStateAction<string | null>>;
  setAlertStatusBySearchTermFilters: Dispatch<
    SetStateAction<SetAlertStatusBySearchTermFiltersInput>
  >;
  onFormDataChange: (formData: AlertCatalogForm) => void;
}

const {
  isActive,
  category,
  risk,
  score,
  search: searchText,
  status,
  type,
  noValueSet,
  learnMore,
  searchHelpText,
} = i18n.pages.system.alertCatalog;

export const AlertCatalogSearch = ({
  setQuery,
  setQueryTerm,
  setPrevQueryTerm,
  queryTerm,
  setAlertStatusBySearchTermFilters,
  onFormDataChange,
}: AlertCatalogSearchProps) => {
  const navigate = useNavigate();

  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);
  const hasOrgTenants =
    orgTenantsQueryParam && orgTenantsQueryParam?.length > 0;

  const searchParams = useQueryParams();

  const prevQueryTerm = usePrevious(queryTerm);
  useEffect(() => {
    setPrevQueryTerm?.(prevQueryTerm || null);
  }, [prevQueryTerm, setPrevQueryTerm]);
  const { COLON, HYPHEN, DOUBLE_QUOTES, FORWARD_SLASH } = REGEX;

  const defaultValues = useMemo(() => {
    return {
      wildcard: '',
      active: '',
      severity: '',
      category: '',
    };
  }, []);

  const { control, setValue, watch, getValues, handleSubmit, reset } =
    useForm<AlertCatalogForm>({
      mode: 'onSubmit',
      defaultValues,
    });

  // Refactored: Only sync form state with searchParams when searchParams actually change
  useEffect(() => {
    const wildcard = searchParams.get('wildcard');
    if (
      wildcard !== null &&
      getValues('wildcard') !== decodeURIComponent(wildcard)
    ) {
      setValue('wildcard', decodeURIComponent(wildcard));
    }
    const active = searchParams.get('active');
    if (
      active !== null &&
      getValues('active') !== safeParse(active)?.join(', ')
    ) {
      setValue(
        'active',
        Array.isArray(safeParse(active)) ? safeParse(active).join(', ') : '',
      );
      setAlertStatusBySearchTermFilters((prev) => {
        const parsed = safeParse(active);
        const newActive = Array.isArray(parsed)
          ? parsed.map((value: unknown) => !!value)
          : [];
        if (isEqual(prev.active, newActive)) return prev;
        return { ...prev, active: newActive };
      });
    }
    const severity = searchParams.get('severity');
    if (
      severity !== null &&
      getValues('severity') !== safeParse(severity)?.join(', ')
    ) {
      setValue(
        'severity',
        Array.isArray(safeParse(severity))
          ? safeParse(severity).join(', ')
          : '',
      );
      setAlertStatusBySearchTermFilters((prev) => {
        const newSeverity = safeParse(severity);
        if (isEqual(prev.severity, newSeverity)) return prev;
        return { ...prev, severity: newSeverity };
      });
    }
    const category = searchParams.get('category');
    if (
      category !== null &&
      getValues('category') !== safeParse(category)?.join(', ')
    ) {
      setValue(
        'category',
        Array.isArray(safeParse(category))
          ? safeParse(category).join(', ')
          : '',
      );
      setAlertStatusBySearchTermFilters((prev) => {
        const newTypes = safeParse(category);
        if (isEqual(prev.types, newTypes)) return prev;
        return { ...prev, types: newTypes };
      });
    }
  }, [searchParams, setValue, setAlertStatusBySearchTermFilters, getValues]);

  const formData = watch();

  useEffect(() => {
    onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  const handleSearchSubmit = (formData: AlertCatalogForm) => {
    let queries = '';

    let query = formData?.wildcard;

    const firstChar = query.substring(0, 1);
    const lastChar = query.substring(query.length - 1);

    const isRegexSearch = firstChar === '/' && lastChar === '/';

    if (!isRegexSearch) {
      if (query.includes(':')) {
        query = query?.replace(COLON, '\\:');
      }
      if (query.includes('-')) {
        query = query?.replace(HYPHEN, '\\-');
      }
      if (query.includes('/')) {
        query = query?.replace(FORWARD_SLASH, '\\\\/');
      }
      if (query.includes('"')) {
        query = query?.replace(DOUBLE_QUOTES, '\\"');
      }
    }

    if (formData?.wildcard) {
      queries += `{"query_string": {"query": "${query}", "fields": ["title"], "default_operator": "AND"}}`;
      setQueryTerm?.(formData?.wildcard);
      navigate({
        pathname: alertCatalog,
        search: `wildcard=${encodeURIComponent(formData?.wildcard)}`,
      });
    } else {
      setQueryTerm?.(null);
      searchParams.delete('wildcard');
    }

    if (formData?.active?.length) {
      const queryString = generateQueryString(
        formData?.active,
        statusOptions,
        (option) => `{"match": { "active": "${!!option?.value}"}}`,
      );

      const activeQuery = `${queries.length ? ',' : ''}${queryString}`;
      queries += activeQuery;
    }

    if (formData?.category?.length) {
      const queryString = generateQueryString(
        formData?.category,
        typeOptions,
        (option) => `{"term": { "logsource.category": "${option?.value}"}}`,
      );
      const activeQuery = `${queries.length ? ',' : ''}${queryString}`;
      queries += activeQuery;
    }

    if (formData?.severity?.length) {
      const queryString = generateQueryString(
        formData?.severity,
        scoreOptions,
        (option) =>
          `{"range": { "severity": { "gte": ${option?.min}, "lte": ${option?.max}}}}`,
      );
      const activeQuery = `${queries.length ? ',' : ''}${queryString}`;
      queries += activeQuery;
    }

    if (queries.length) {
      setQuery?.(
        `{"query": {"bool": {"must": [${queries}${
          hasOrgTenants && orgTenantsQueryParam?.length === 1 ? ',' : ''
        }${
          hasOrgTenants && orgTenantsQueryParam?.length === 1
            ? `{"terms":{"tenant":${JSON.stringify(orgTenantsQueryParam)}}}`
            : ''
        }]}}}`,
      );
    } else {
      setQuery?.(
        hasOrgTenants && orgTenantsQueryParam?.length === 1
          ? `{"query": {"bool": {"must": [{"terms":{"tenant":${JSON.stringify(
              orgTenantsQueryParam,
            )}}}]}}}`
          : null,
      );
    }

    navigate(
      {
        pathname: alertCatalog,
        search: searchParams.toString(),
      },
      {
        replace: true,
      },
    );
  };

  useEffect(() => {
    if (isEqual(formData, defaultValues)) {
      setQuery?.(
        hasOrgTenants && orgTenantsQueryParam?.length === 1
          ? `{"query": {"bool": {"must": [{"terms":{"tenant":${JSON.stringify(
              orgTenantsQueryParam,
            )}}}]}}}`
          : null,
      );
      setQueryTerm?.(null);
    }
  }, [
    defaultValues,
    formData,
    setQuery,
    setQueryTerm,
    hasOrgTenants,
    orgTenantsQueryParam,
  ]);

  useEffect(() => {
    reset(defaultValues);
  }, [orgTenantsQueryParam, reset, defaultValues]);

  return (
    <Column data-testid="alert-catalog-search">
      <Panel>
        <VStack spacing={4} alignItems="start">
          <HStack
            w="100%"
            as="form"
            borderRadius="base"
            onSubmit={handleSubmit(handleSearchSubmit)}
          >
            <Controller
              control={control}
              name="wildcard"
              render={({ field: { onChange, value, ref } }) => (
                <Input
                  name="wildcard"
                  label="Search"
                  isLabelHidden
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    if (e.target.value === '') {
                      searchParams.delete('wildcard');
                      navigate(
                        {
                          pathname: alertCatalog,
                          search: searchParams.toString(),
                        },
                        {
                          replace: true,
                        },
                      );
                    }
                  }}
                  leftElement={<SearchSharpIcon />}
                  data-testid="search-term-input"
                  placeholder={i18n.pages.system.alertCatalog.alertName}
                  rightElement={
                    <IconButton
                      data-testid="search-term-clear"
                      aria-label="close"
                      onClick={() => {
                        setValue('wildcard', '');
                        handleSubmit(handleSearchSubmit)();
                      }}
                      variant="link"
                      colorScheme="gray"
                    >
                      <CloseSharpIcon />
                    </IconButton>
                  }
                  ref={ref}
                />
              )}
            />
            <Button
              type="submit"
              variant="solid"
              alignSelf="flex-start"
              data-testid="search-term-submit"
            >
              {searchText}
            </Button>
          </HStack>
          <HStack>
            <Text>{searchHelpText}</Text>
            <Link color="text.link" isExternal href={URLS.ALERT_CATALOG_SEARCH}>
              {learnMore}
            </Link>
          </HStack>
          <Flex
            w="100%"
            zIndex={2}
            position="relative"
            alignItems="center"
            justifyContent="space-between"
          >
            <HStack>
              <Controller
                control={control}
                name={'active'}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <Select
                      isMulti
                      name={name}
                      label={name}
                      data-testid="active-select"
                      value={getSelectedValues(value, statusOptions)}
                      onChange={(values) => {
                        onChange(createOptionString(values));
                        handleSubmit(handleSearchSubmit)();
                        const active = values.map((value) => !!value.value);
                        setAlertStatusBySearchTermFilters((prev) => ({
                          ...prev,
                          active,
                        }));
                        if (active.length === 0) {
                          searchParams.delete('active');
                        }
                      }}
                      isSearchable={false}
                      isDisabled={false}
                      isLabelHidden={true}
                      options={statusOptions}
                      leftElement={status}
                      minWidth="200px"
                      width="fit-content"
                      tagVariant="solid"
                      placeholder={noValueSet}
                      components={customOption}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={'severity'}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <Select
                      isMulti
                      name={name}
                      label={name}
                      data-testid="severity-select"
                      value={getSelectedValues(value, scoreOptions)}
                      onChange={(values) => {
                        onChange(createOptionString(values));
                        handleSubmit(handleSearchSubmit)();
                        const severity = values.map(
                          (value: unknown) =>
                            SetAlertStatusScore[
                              value as keyof typeof SetAlertStatusScore
                            ],
                        );
                        setAlertStatusBySearchTermFilters((prev) => ({
                          ...prev,
                          severity,
                        }));
                        if (severity.length === 0) {
                          searchParams.delete('severity');
                        }
                      }}
                      isSearchable={false}
                      isDisabled={false}
                      isLabelHidden={true}
                      options={scoreOptions}
                      leftElement={score}
                      minWidth="200px"
                      width="fit-content"
                      tagVariant="solid"
                      placeholder={noValueSet}
                      components={customOption}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={'category'}
                render={({ field: { onChange, name, value } }) => {
                  return (
                    <Select
                      isMulti
                      name={name}
                      label={name}
                      data-testid="category-select"
                      value={getSelectedValues(value, typeOptions)}
                      onChange={(values) => {
                        onChange(createOptionString(values));
                        handleSubmit(handleSearchSubmit)();
                        const types = values.map(
                          (value: unknown) =>
                            SetAlertStatusType[
                              value as keyof typeof SetAlertStatusType
                            ],
                        );
                        setAlertStatusBySearchTermFilters((prev) => ({
                          ...prev,
                          types,
                        }));
                        if (types.length === 0) {
                          searchParams.delete('category');
                        }
                      }}
                      isSearchable={false}
                      isDisabled={false}
                      isLabelHidden={true}
                      options={typeOptions}
                      leftElement={type}
                      minWidth="200px"
                      width="fit-content"
                      tagVariant="solid"
                      placeholder={noValueSet}
                      components={customOption}
                    />
                  );
                }}
              />
            </HStack>
          </Flex>
        </VStack>
      </Panel>
    </Column>
  );
};

const CustomSelectOption = <
  IsMulti extends boolean,
  Option extends OptionType,
  Group extends GroupBase<Option>,
>({
  children,
  ...rest
}: OptionProps<Option, IsMulti, Group>) => (
  <chakraComponents.Option {...rest}>
    {rest.data.severity} {children}
  </chakraComponents.Option>
);

const customOption = {
  Option: CustomSelectOption,
};

const createOptionString = <T extends OptionType>(options: MultiValue<T>) => {
  const values = options.map(({ value }) => value);
  return values.join(', ');
};

const getSelectedValues = <T extends OptionType>(
  values: string,
  options: T[],
) => {
  const splitString = values.split(', ');
  const optionsArray = splitString.map((string) => {
    return options.find((option) => option.value.toString() === string);
  });
  const filteredArray = optionsArray.filter((option) => option);
  return filteredArray as T[];
};

const generateQueryString = <T extends OptionType>(
  value: string,
  options: T[],
  callback: (option: T) => string,
) => {
  let queryValues = '';
  const selectedOptions = getSelectedValues(value, options);

  selectedOptions.forEach((option, index) => {
    queryValues += selectedOptions.length > 1 && index === 0 ? `[` : '';
    queryValues += selectedOptions.length > 1 && index !== 0 ? ',' : '';
    queryValues += callback(option);
    queryValues +=
      selectedOptions.length > 1 && index === selectedOptions.length - 1
        ? `]`
        : '';
  });
  const activeQuery = `{"bool": {"should": ${queryValues}}}`;
  return activeQuery;
};

const statusOptions: StatusOption[] = [
  { value: 1, label: isActive.active },
  { value: 0, label: isActive.inactive },
];

const scoreOptions: ScoreOption[] = [
  {
    value: 'high',
    label: risk.high,
    min: 7,
    max: 10,
    severity: (
      <Text
        w="52px"
        textStyle="body-md"
        color="red.400"
        data-testid="score-option-high"
      >
        {i18n.formatString(i18n.pages.system.alertCatalog.range, 7, 10)}
      </Text>
    ),
  },
  {
    value: 'medium',
    label: risk.medium,
    min: 4,
    max: 6,
    severity: (
      <Text
        w="52px"
        textStyle="body-md"
        color="yellow.400"
        data-testid="score-option-medium"
      >
        {i18n.formatString(i18n.pages.system.alertCatalog.range, 4, 6)}
      </Text>
    ),
  },
  {
    value: 'low',
    label: risk.low,
    min: 1,
    max: 3,
    severity: (
      <Text
        w="52px"
        textStyle="body-md"
        color="cyan.400"
        data-testid="score-option-low"
      >
        {i18n.formatString(i18n.pages.system.alertCatalog.range, 1, 3)}
      </Text>
    ),
  },
];

const typeOptions: TypeOption[] = [
  { value: 'ml_results', label: category.ml },
  { value: 'notice', label: category.notice },
  { value: 'custom_search_rule', label: category.searchBased },
  { value: 'suricata_corelight', label: category.suricata },
  { value: 'yara_corelight', label: category.yara },
  { value: 'anomaly', label: category.anomaly },
];
